/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';

import { Column, Row } from 'components/Blocks/Containers';
import { ScrollArea } from 'components/Blocks/ScrollArea';
import { Checkbox } from 'components/Blocks/Inputs';
import { FilterIcon } from 'components/Blocks/Icons';

import { useFormControlV2 } from 'hooks';

import ActionTitle from './Title';
import ApplyButton from './ApplyButton';
import ClearButton from './ClearButton';
import SearchInput from './SearchInput';
import { TableAction } from './Trigger';

import { useTable } from '../../Table';
import { TableColumn } from '../../types';

import { getColumnKey, getEntryValue } from './utils';
import { isSubstring } from 'utils/text';
import { bestEffortSort } from 'utils/array';
import { overlayEscape } from 'components/Blocks/Overlays';

interface FilterButtonProps {
  column: TableColumn<any>;
  index: number;
}
function getDisplayValue(value: any): string {
  if (typeof value !== 'boolean') return `${value}`;

  return value ? 'Yes' : 'No';
}

export function FilterButton({ column, index }: FilterButtonProps): JSX.Element {
  const { filters, dispatch, items } = useTable();

  const { filterSearch = true, filterSort } = column;

  const filterKey = getColumnKey(column, index);
  const selectedFilters = filters[filterKey] ?? [];

  const [localState, localHandlers] = useFormControlV2({
    search: '',
    filters: [] as any[],
  });

  const entries = useMemo(() => {
    const filterOptions = Object.fromEntries(
      items
        .map((item) => {
          const refValue = getEntryValue(item, column, filterKey);

          if (typeof refValue === 'undefined') return null;

          if (!isSubstring(localState.search, refValue)) return null;

          return [`${refValue}`, refValue];
        })
        .filter(Boolean) as [string, any][],
    );

    const options = Object.values(filterOptions);

    return filterSort ? options.slice().sort(filterSort) : bestEffortSort(options);
  }, [items, filterKey, localState.search, column, filterSort]);

  const applyFilters = (): void => {
    dispatch({
      filters: (old) => ({
        ...old.filters,
        [filterKey]: localState.filters,
      }),
    });

    overlayEscape();
  };

  const clear = (): void => {
    localHandlers.dispatch({ filters: [] });

    dispatch({
      filters: (old) => ({
        ...old.filters,
        [filterKey]: [],
      }),
    });

    overlayEscape();
  };

  const active = !!selectedFilters.length;

  return (
    <TableAction
      icon={<FilterIcon size={10} />}
      isActive={active}
      onOpen={() => localHandlers.dispatch({ filters: selectedFilters })}
    >
      <Column>
        <Row justifyContent="space-between">
          <ActionTitle>Filter</ActionTitle>

          {active && <ClearButton onClick={clear} />}
        </Row>

        {filterSearch && (
          <SearchInput
            value={localState.search}
            onChange={({ target }) => localHandlers.dispatch({ search: target.value })}
          />
        )}

        <ScrollArea orientation="vertical" maxHeight={220}>
          <Column gap="1">
            {entries.map((entry) => (
              <Row key={`${entry}`} gap="2" className="single-entry">
                <Checkbox
                  id={`${filterKey}-${entry}`}
                  checked={localState.filters.includes(entry)}
                  size="small"
                  onChange={() =>
                    localHandlers.dispatch({
                      filters: (old) =>
                        old.filters.includes(entry)
                          ? old.filters.filter((x) => x !== entry)
                          : [...old.filters, entry],
                    })
                  }
                />

                <label htmlFor={`${filterKey}-${entry}`} className="text-sm">
                  {getDisplayValue(entry)}
                </label>
              </Row>
            ))}
          </Column>
        </ScrollArea>

        <ApplyButton type="button" onClick={applyFilters} />
      </Column>
    </TableAction>
  );
}
